<script lang="ts" setup>
import Button1 from '@/components/Button/Button1.vue'
import FaqItem from './components/FaqItem.vue'
import Contact from './components/Contact.vue'
import Pagination from '@/components/Pagination.vue'
import _ from 'lodash'
import { useOptionStore, useArticleStore } from '@/stores'
import type { IFaqFilter } from '@/utils/types/Filter'
import useSeo from './seo';

const route = useRoute()
const router = useRouter()
const optionStore = useOptionStore()
const articleStore = useArticleStore()
const searchOpen = ref(false)
const search = reactive<IFaqFilter>({
  brand_id: route.query.brand_id as string ?? '',
  item_id: route.query.item_id as string ?? '',
  model_id: route.query.model_id as string ?? '',
  category_id: route.query.category_id as string ?? '',
  q: route.query.q as string ?? '',
  page: parseInt(route.query.page as string || '1')
})

await optionStore.fetchProductInofs()
await articleStore.fetchArticleCategories('faqCategories', {
  className: 'articleFaq'
})

const brands = computed(() => optionStore.productBrandOptions('all'))
const items = computed(() => optionStore.productItemOptions('all', search.brand_id))
const models = computed(() => optionStore.productModelOptions('all', search.brand_id, search.item_id))
const getFaqs = async () => {
  await articleStore.fetchArticleFaqs(search)
}
getFaqs()
const goSearch = () => {
  const url = '/services/faq?' + new URLSearchParams(filterObject(search, '')).toString()
  router.push(url)
  getFaqs()
}

const handleChangePage = (page: number) => {
  search.page = page
  getFaqs()
}

useSeo(search)
</script>

<template>
  <div class="faq">
    <div class="faq__left">
      <Intro subtitle="FAQ" :data="{
        title: '常見問題'
      }" />
      <div class="faq__left__drop">
        <div class="faq__left__drop__top" :class="{ open: searchOpen }" @click="searchOpen = !searchOpen">
          <div class="faq__left__drop__top__title">問題搜尋/分類</div>
          <div class="faq__left__drop__top__icon">
            <svg-icon :name="searchOpen ? 'icon-close' : 'icon-search'" />
          </div>
        </div>
        <div class="faq__left__drop__content" :class="{ open: searchOpen }">
          <Searchbar class="white faq__search" icon="" v-model="search.q" placeholder="輸入關鍵字..." />
          <el-select v-model="search.brand_id" class="h-select w-full" placeholder="品牌"
            @change="() => { search.item_id = ''; search.model_id = '' }">
            <el-option :label="item.subject" :value="item.id.toString()" v-for="item in brands" />
          </el-select>
          <el-select v-model="search.item_id" class="h-select w-full" placeholder="商品品項"
            @change="() => { search.model_id = '' }">
            <el-option :label="item.subject" :value="item.id.toString()" v-for="item in items" />
          </el-select>
          <el-select v-model="search.model_id" class="h-select w-full" placeholder="商品型號">
            <el-option :label="item.subject" :value="item.id.toString()" v-for="item in models" />
          </el-select>
          <el-select v-model="search.category_id" class="h-select w-full" placeholder="常見問題/產品教學">
            <el-option :label="item.subject" :value="item.id.toString()" v-for="item in articleStore.faqCategories" />
          </el-select>
          <Button1 class="faq__left__button" linkTitle="送出搜尋" icon="icon-search" @click="goSearch" />
        </div>
      </div>
    </div>
    <div class="faq__right">
      <ul class="faq__lists">
        <li v-for="(item, index) in articleStore.faqs" :key="index">
          <FaqItem :data="item" />
        </li>
      </ul>
      <div class="faq__pagination">
        <Pagination
          v-if="articleStore.pageMeta !== null && articleStore.pageMeta?.per_page <= articleStore.pageMeta?.total"
          class="h-pagination center" layout="prev, pager, next" v-model:page="search.page"
          :per-page="articleStore.pageMeta?.per_page" :total="articleStore.pageMeta?.total"
          :handleChangePage="handleChangePage" />
      </div>
    </div>
    <div class="faq__bottom">
      <Contact class="faq__bottom__contact" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.faq {
  @include grid;

  @include max-media(990) {
    @include grid(4, 24);
  }

  &__left {
    grid-column: 1 / span 2;

    @include max-media(1366) {
      grid-column: 1 / span 3;
    }

    @include max-media(990) {
      grid-column: 1 / span 4;
    }

    .intro {
      text-align: left;

      :deep(.intro__subtitle) {
        font-size: toRem(80);
      }
    }

    .h-select {
      margin-bottom: toRem(12);
    }

    &__button {
      width: 100%;
      margin-top: toRem(18);
    }

    &__drop {
      position: relative;
      z-index: 1;

      &__top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: toRem(50);
        border-radius: toRem(25);
        padding-left: toRem(24);
        padding-right: toRem(24);
        background-color: $color-gray-2;
        color: $color-white-1;

        @include min-media(641) {
          display: none;
        }

        &__title {
          font-size: toRem(16);
          font-weight: 700;
          line-height: 1.6;
        }

        &__icon {
          width: toRem(24);
          height: toRem(24);
        }
      }

      &__content {
        @include max-media(640) {
          position: absolute;
          left: 0;
          top: calc(100% - toRem(25));
          z-index: -1;
          padding: toRem(49) toRem(24) toRem(24);
          background-color: $color-gray-2;
          border-radius: 0 0 toRem(25) toRem(25);
          opacity: 0;
          pointer-events: none;
          transition: all 0.3s;

          &.open {
            opacity: 1;
            pointer-events: auto;
          }
        }
      }
    }
  }

  &__search {
    margin-bottom: toRem(30);
  }

  &__right {
    grid-column: 4 / span 6;
    padding-top: toRem(150);
    margin-bottom: toRem(200);

    @include max-media(1366) {
      grid-column: 5 / span 7;
    }

    @include max-media(990) {
      grid-column: 1 / span 4;
      padding-top: toRem(24);
      margin-bottom: toRem(80);
    }
  }

  &__lists {
    border-top: 1px solid $color-gray-line;
    margin-bottom: toRem(100);

    @include max-media(990) {
      margin-bottom: toRem(80);
    }
  }

  &__bottom {
    @include grid;
    grid-column: 1 / span 12;

    @include max-media(990) {
      @include grid(4, 24);
      grid-column: 1 / span 4;
    }

    &__contact {
      grid-column: 3 / span 8;

      @include max-media(1200) {
        grid-column: 2 / span 10;
      }

      @include max-media(990) {
        grid-column: 1 / span 4;
      }
    }
  }
}
</style>
